import { useState } from "react";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { useUser } from "../../contexts/UserContext";
import Alert from "@mui/material/Alert";

/**
 * Custom hook for managing tasks (customer notes) in Obligo orders
 * Handles creating new tasks and appending messages to existing tasks
 */
export const useTaskManager = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newTask, setNewTask] = useState({ text: "" });
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const { user } = useUser();

  /**
   * Creates formatted HTML message for task with user signature
   * @param {string} text - The message text
   * @param {string} UserSignature - User's full name
   * @param {string} email - User's email address
   * @returns {string} Formatted HTML message
   */
  const createFormatMsg = (text, UserSignature, email) => {
    const date = getCurrentFormattedDate();
    let format = `<style> p,div,li {margin:0cm;font-size:10.0pt;font-family:'Arial';}</style><P dir=rtl>&nbsp;</P> <HR ><P dir=rtl><FONT color=#0000ff>תאריך ${date}<!--edt--><BR>${text}</FONT></P> <P dir=rtl><!--psi--><FONT color=#0000ff>בברכה,<BR>${UserSignature}<BR>דוא\"ל: ${email}</FONT><!--esi--></P>`
    return format;
  };

  /**
   * Gets current date and time in Hebrew locale format
   * @returns {string} Formatted date/time string (HH:MM DD/MM/YY)
   */
  const getCurrentFormattedDate = () => {
    const currentDate = new Date();
    const time = currentDate.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const date = currentDate
      .toLocaleDateString("he-IL", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
      .replace(/\./g, "/");
    return `${time} ${date}`;
  };

  /**
   * Creates a new task (customer note) for an order
   * @param {string} ordname - Order name/ID
   * @param {string} doerName - Optional doer name
   * @param {Function} onSuccess - Callback function called on success with new task
   * @returns {Promise<Object>} The created task object
   */
  const addTask = async (ordname, doerName, onSuccess) => {
    setLoading(true);
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/addTasks`,
        {
          ordname: ordname,
          doerName: doerName,
          text: newTask.text,
        }
      );

      if (response) {
        const myTask = {
          CUSTNOTE: response.result.CUSTNOTE,
          CUSTNOTESTEXT_SUBFORM: {
            TEXT: createFormatMsg(
              newTask.text,
              response.UserSignature,
              response.email
            ),
          },
          TOPICCODE: "711",
        };

        setNewTask({ text: "" });
        if (onSuccess) {
          onSuccess(myTask);
        }
        return myTask;
      }
    } catch (error) {
      setError(
        <Alert severity="error" onClose={() => setError(null)}>
          אירעה תקלה, אנא נסו שנית מאוחר יותר
        </Alert>
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets task form state and clears errors
   */
  const resetTask = () => {
    setNewTask({ text: "" });
    setError(null);
  };

  /**
   * Appends a message to an existing task
   * @param {string} ordname - Order name/ID
   * @param {string} custnote - Customer note ID
   * @param {string} text - Message text to append
   * @param {Function} onSuccess - Callback function called on success with formatted message
   */
  const addMessage = async (ordname, custnote, text, onSuccess) => {
    setLoading(true);
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/addTextToTasks`,
        {
          ordname: ordname,
          custnote: custnote,
          text: text,
        }
      );
      if (onSuccess) {
        const formattedMessage = createFormatMsg(text, user.fullName, user.email);
        onSuccess(formattedMessage);
      }
    } catch (errorMsg) {
      setError(
        <Alert severity="error" onClose={() => setError(null)}>
          אירעה תקלה, אנא נסו שנית מאוחר יותר
        </Alert>
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    newTask,
    setNewTask,
    loading,
    error,
    addTask,
    addMessage,
    resetTask,
    createFormatMsg,
  };
};